import { FlashbarProps } from '@cloudscape-design/components';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import React, { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

import {
  GetWebsiteEventsDocument,
  GetWebsiteEventsQuery,
  useCreateWebsiteEventsMutation,
  WebsiteEvents,
} from '../../../../generated/graphql';
import { ErrorText } from '../../../../lib/ErrorTexts';
import Form from './Form';
import { createDDBItem } from './lib/Helper';

type CreateProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  notifyDeletedOrUpdated: (resource: FlashbarProps.MessageDefinition) => void;
  notifyInProgress: (resource?: FlashbarProps.MessageDefinition) => void;
  notifyFailed: (failedResource: FlashbarProps.MessageDefinition) => void;
};

export type FormValues = {
  title: string;
  location: string;
  range: { type?: string | null; startDate: string; endDate: string };
  is_active: boolean;
};

export const schema = yup
  .object({
    title: yup.string().required(ErrorText.Required),
    location: yup.string().required(ErrorText.Required),
    range: yup
      .object({
        type: yup.string().notRequired(),
        startDate: yup.string().required(ErrorText.StartDate),
        endDate: yup.string().required(ErrorText.EndDate),
      })
      .required(ErrorText.Required),
    is_active: yup.boolean().required(ErrorText.Required),
  })
  .required();

export default function Create({
  visible,
  setVisible,
  notifyInProgress,
  notifyDeletedOrUpdated,
  notifyFailed,
}: CreateProps) {
  const [imageUrl, setImageUrl] = useState<string | undefined>();
  const [createWebsiteEvents, { loading: createLoading }] =
    useCreateWebsiteEventsMutation();
  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {},
    resolver: yupResolver(schema),
  });
  const onSubmit = (data: any) => {
    const entry = createDDBItem(data);
    if (imageUrl) {
      entry['imageurl'] = imageUrl;
    }
    const messageId = uuidv4();
    notifyInProgress({ id: messageId, header: 'Eintrag wird erstellt...' });
    createWebsiteEvents({
      variables: { input: entry },
      update: (cache, { data }) => {
        const addData = data?.createWebsiteEvents;
        const existingData = cache.readQuery({
          query: GetWebsiteEventsDocument,
        }) as GetWebsiteEventsQuery;
        const oldData = existingData.listWebsiteEvents
          ?.items as WebsiteEvents[];

        cache.writeQuery({
          query: GetWebsiteEventsDocument,
          data: {
            listWebsiteEvents: { items: [...oldData, addData] },
          },
        });
      },
    })
      .then((data) => {
        const result = data.data?.createWebsiteEvents;
        if (result) {
          notifyInProgress();
          notifyDeletedOrUpdated({
            id: messageId,
            type: 'success',
            content: `Eintrag mit dem Namen "${result.title}" wurde erstellt.`,
            header: 'Gespeichert',
            dismissible: true,
          });
          handleCancel();
        }
      })
      .catch((error) => {
        notifyInProgress();
        notifyFailed({
          id: messageId,
          content: JSON.stringify(error),
          header: 'Ein Fehler ist aufgetreten',
          dismissible: true,
        });
      });
  };

  const handleCancel = useCallback(() => {
    setVisible(false);
    methods.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.reset, setVisible]);

  const handleImageUrlChange = useCallback((url?: string) => {
    setImageUrl(url);
  }, []);

  return (
    <FormProvider {...methods}>
      <Modal
        onDismiss={handleCancel}
        visible={visible}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCancel}>
                Abbrechen
              </Button>
              <Button
                variant="primary"
                disabled={
                  isEmpty(methods.formState.dirtyFields) || createLoading
                }
                formAction="submit"
                onClick={() => methods.handleSubmit(onSubmit)()}
              >
                Speichern
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={'Eintrag erstellen'}
      >
        <Form changeImageUrl={handleImageUrlChange} />
      </Modal>
    </FormProvider>
  );
}
